/**
 * nb-ui.js: shared behaviour modules, contract in specs/NB-UI.md.
 * No library. Each module scoped to its root, safe with several instances on one page.
 * Inits on DOMContentLoaded and on shopify:section:load. Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function each(sel, root, fn) {
    (root || document).querySelectorAll(sel).forEach(fn);
  }

  function fire(el, name, detail) {
    el.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  }

  // Per-module init marker (2026-09-24). A single shared `data-nb-init` flag meant that
  // once one module marked a root as initialised, any OTHER module sharing that same root
  // silently never ran: whichever module's init call happened to run first in initAll()
  // consumed the one flag for everyone. Hit twice in practice: the cart drawer (a root that
  // also matched a second selector) and the expo marquee (whose standing track root can
  // legitimately also carry data-nb-expo-window, the show-day window logic, so initMarquee
  // running first silently blocked initExpoWindow from ever setting data-nb-expo-state).
  // Each module now gets its own `data-nb-init-<module>` attribute, so two modules on one
  // root never contend for the same flag. Always pass a short, stable module name.
  function markInit(el, module) {
    var attr = 'data-nb-init-' + module;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '');
    return true;
  }

  /* ---------------- accordion ---------------- */
  function initAccordion(root) {
    if (!markInit(root, 'accordion')) return;
    var mode = root.getAttribute('data-nb-accordion-mode');
    var rows = root.querySelectorAll('[data-nb-accordion-row]');
    rows.forEach(function (row) {
      row.addEventListener('toggle', function () {
        fire(row, row.open ? 'nb:open' : 'nb:close');
        if (row.open && mode === 'single') {
          rows.forEach(function (other) {
            if (other !== row && other.open) other.open = false;
          });
        }
      });
    });
  }

  /* ---------------- carousel ---------------- */
  function initCarousel(root) {
    if (!markInit(root, 'carousel')) return;
    var mode = root.getAttribute('data-nb-carousel-mode') || 'fade';
    var track = root.hasAttribute('data-nb-carousel-track') ? root : root.querySelector('[data-nb-carousel-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-nb-carousel-slide]'));
    var dots = root.querySelectorAll('[data-nb-carousel-dot]');
    var arrow = root.querySelector('[data-nb-carousel-arrow]');
    var autoplayMs = parseInt(root.getAttribute('data-nb-carousel-autoplay') || '0', 10);
    var index = 0;
    var timer = null;

    function goTo(i) {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) {
        s.classList.toggle('is-active', n === index);
      });
      dots.forEach(function (d, n) {
        var active = n === index;
        d.classList.toggle('bg-cobalt', active);
        d.classList.toggle('bg-white', active && root.querySelector('[data-nb-dots][data-tone="white"]'));
        if (active) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      if (arrow) arrow.hidden = index === slides.length - 1;
      if (mode === 'snap' && track) {
        var target = slides[index];
        /* go-live gutters, 2026-09-28: offsetLeft includes the track's own left padding
           (the page gutter), so scrolling straight to it strips that gutter and leaves
           the first (and every) slide flush against the edge. Subtract the track's
           current padding-left so the gutter always survives the scroll. */
        var trackPad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        var left = Math.max(0, target.offsetLeft - trackPad);
        track.scrollTo({ left: left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
      fire(root, 'nb:change', { index: index });
    }

    root.querySelectorAll('[data-nb-carousel-goto]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        goTo(parseInt(btn.getAttribute('data-nb-carousel-goto'), 10));
      });
    });
    if (arrow) arrow.addEventListener('click', function () { goTo(index + 1); });

    if (mode === 'snap' && track) {
      var scrollTimeout;
      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var closest = 0;
          var min = Infinity;
          slides.forEach(function (s, n) {
            var d = Math.abs(s.offsetLeft - track.scrollLeft);
            if (d < min) { min = d; closest = n; }
          });
          index = closest;
          goTo(index);
        }, 100);
      }, { passive: true });
    }

    if (autoplayMs && !reduceMotion.matches) {
      function start() { timer = setInterval(function () { goTo(index + 1); }, autoplayMs); }
      function stop() { clearInterval(timer); }
      start();
      root.addEventListener('pointerenter', stop);
      root.addEventListener('pointerleave', start);
      root.addEventListener('focusin', stop);
      root.addEventListener('focusout', start);
    }

    goTo(0);
  }

  /* ---------------- sheet / drawer (shared open/close/focus-trap machinery) ---------------- */
  function getFocusable(root) {
    return Array.prototype.slice.call(
      root.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')
    );
  }

  function makePanelController(attrName, closeAttr, extraOnOpen) {
    var openPanels = [];

    function ensureScrim(panel) {
      var scrimAttr = attrName === 'data-nb-sheet' ? 'data-nb-sheet-scrim' : 'data-nb-drawer-scrim';
      var scrim = document.querySelector('[' + scrimAttr + ']');
      if (!scrim) {
        scrim = document.createElement('div');
        scrim.setAttribute(scrimAttr, '');
        document.body.appendChild(scrim);
      }
      return scrim;
    }

    function open(panel, trigger) {
      var scrim = ensureScrim(panel);
      // Move the panel to the end of <body>, outside #main, while it is open, and
      // remember exactly where to put it back. A sheet or drawer that lives inside #main
      // (the bundle-card sheets, the hero video drawer) used to be hidden from screen
      // readers along with the rest of #main when nb-ui set aria-hidden on it (N4,
      // 2026-09-24): the open dialog was inside the very subtree being hidden. Moving it
      // out means #main can be hidden freely and the open dialog is never inside it.
      if (panel.parentNode !== document.body) {
        panel._nbHomeParent = panel.parentNode;
        panel._nbHomeNextSibling = panel.nextSibling;
        document.body.appendChild(panel);
      }
      panel.hidden = false;
      requestAnimationFrame(function () {
        panel.classList.add('is-open');
        scrim.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
      var main = document.querySelector('#main');
      if (main) main.setAttribute('aria-hidden', 'true');
      panel._nbTrigger = trigger;
      openPanels.push(panel);
      var focusables = getFocusable(panel);
      if (focusables.length) focusables[0].focus();
      if (extraOnOpen) extraOnOpen(panel);

      function onScrimClick() { close(panel); }
      scrim.addEventListener('click', onScrimClick, { once: true });

      function onKeydown(e) {
        if (e.key === 'Escape') { close(panel); return; }
        if (e.key === 'Tab') {
          var items = getFocusable(panel);
          if (!items.length) return;
          var first = items[0], last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
      panel._nbKeydown = onKeydown;
      document.addEventListener('keydown', onKeydown);
      fire(panel, 'nb:open');
    }

    function close(panel) {
      panel.classList.remove('is-open');
      var scrimAttr = attrName === 'data-nb-sheet' ? 'data-nb-sheet-scrim' : 'data-nb-drawer-scrim';
      var scrim = document.querySelector('[' + scrimAttr + ']');
      if (scrim) scrim.classList.remove('is-open');
      document.body.style.overflow = '';
      var main = document.querySelector('#main');
      if (main) main.removeAttribute('aria-hidden');
      if (panel._nbKeydown) document.removeEventListener('keydown', panel._nbKeydown);
      openPanels = openPanels.filter(function (p) { return p !== panel; });
      var trigger = panel._nbTrigger;
      setTimeout(function () {
        panel.hidden = true;
        // Return the panel to where it started in the document. A section that re-renders
        // (shopify:section:load) will replace it anyway, but returning it keeps the DOM
        // consistent for anything that queries its original position while closed.
        if (panel._nbHomeParent) {
          if (panel._nbHomeNextSibling && panel._nbHomeNextSibling.parentNode === panel._nbHomeParent) {
            panel._nbHomeParent.insertBefore(panel, panel._nbHomeNextSibling);
          } else {
            panel._nbHomeParent.appendChild(panel);
          }
          panel._nbHomeParent = null;
          panel._nbHomeNextSibling = null;
        }
        if (trigger && trigger.focus) trigger.focus();
      }, reduceMotion.matches ? 0 : 250);
      fire(panel, 'nb:close');
    }

    return { open: open, close: close };
  }

  var sheetCtl = makePanelController('data-nb-sheet', 'data-nb-sheet-close', function (panel) {
    var handle = panel.querySelector('[data-nb-sheet-handle]');
    if (!handle) return;
    var startY = null;
    function onStart(e) { startY = (e.touches ? e.touches[0].clientY : e.clientY); }
    function onMove(e) {
      if (startY === null) return;
      var y = (e.touches ? e.touches[0].clientY : e.clientY);
      var delta = y - startY;
      if (delta > 0) panel.style.transform = 'translateY(' + delta + 'px)';
    }
    function onEnd(e) {
      if (startY === null) return;
      var y = (e.changedTouches ? e.changedTouches[0].clientY : e.clientY);
      var delta = y - startY;
      panel.style.transform = '';
      startY = null;
      if (delta > panel.offsetHeight * 0.4) sheetCtl.close(panel);
    }
    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: true });
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('mousedown', onStart);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('mousemove', onMove);
  });

  var drawerCtl = makePanelController('data-nb-drawer', 'data-nb-drawer-close');

  function initSheets(root) {
    each('[data-nb-sheet]', root, function (panel) {
      if (!markInit(panel, 'sheet')) return;
      if (!panel.hasAttribute('data-nb-drawer-side') && panel.matches('[data-nb-sheet]')) {
        // no side needed for sheet
      }
      each('[data-nb-sheet-close]', panel, function (btn) {
        btn.addEventListener('click', function () { sheetCtl.close(panel); });
      });
    });
    each('[data-nb-sheet-open]', root, function (trigger) {
      if (trigger.hasAttribute('data-nb-sheet-open-init')) return;
      trigger.setAttribute('data-nb-sheet-open-init', '');
      trigger.addEventListener('click', function (e) {
        var id = trigger.getAttribute('data-nb-sheet-open');
        var panel = document.getElementById(id);
        if (panel) {
          // The trigger may be a real <a href> (its href is the no-JS fallback): once JS
          // is handling the open, the click must not also navigate there (QC-H010, the
          // same bug class as the drawer trigger below).
          e.preventDefault();
          sheetCtl.open(panel, trigger);
        }
      });
    });
  }

  function initDrawers(root) {
    each('[data-nb-drawer]', root, function (panel) {
      if (!markInit(panel, 'drawer')) return;
      if (!panel.hasAttribute('data-nb-drawer-side')) panel.setAttribute('data-nb-drawer-side', 'right');
      each('[data-nb-drawer-close]', panel, function (btn) {
        btn.addEventListener('click', function () { drawerCtl.close(panel); });
      });
    });
    each('[data-nb-drawer-open]', root, function (trigger) {
      if (trigger.hasAttribute('data-nb-drawer-open-init')) return;
      trigger.setAttribute('data-nb-drawer-open-init', '');
      trigger.addEventListener('click', function (e) {
        var id = trigger.getAttribute('data-nb-drawer-open');
        var panel = document.getElementById(id);
        if (panel) {
          // QC-H010: the header cart icon is a real <a href="/cart"> (the no-JS
          // fallback). Without preventDefault the click both opened the drawer AND
          // navigated to /cart, so the drawer never had a chance to show.
          e.preventDefault();
          drawerCtl.open(panel, trigger);
        }
      });
    });
  }

  /* ---------------- dropdown (mega menu) ---------------- */
  var openDropdown = null;
  function initDropdown(root) {
    if (!markInit(root, 'dropdown')) return;
    var trigger = root.querySelector('[data-nb-dropdown-trigger]');
    var panel = root.querySelector('[data-nb-dropdown-panel]');
    if (!trigger || !panel) return;
    var openTimer, closeTimer;

    function openIt() {
      if (openDropdown && openDropdown !== root) closeIt(openDropdown);
      panel.hidden = false;
      requestAnimationFrame(function () { panel.classList.add('is-open'); });
      trigger.setAttribute('aria-expanded', 'true');
      openDropdown = root;
      fire(root, 'nb:open');
    }
    function closeIt(target) {
      var t = target || root;
      var p = t.querySelector('[data-nb-dropdown-panel]');
      var trig = t.querySelector('[data-nb-dropdown-trigger]');
      if (!p) return;
      p.classList.remove('is-open');
      if (trig) trig.setAttribute('aria-expanded', 'false');
      setTimeout(function () { p.hidden = true; }, reduceMotion.matches ? 0 : 150);
      if (openDropdown === t) openDropdown = null;
      fire(t, 'nb:close');
    }

    root.addEventListener('pointerenter', function () {
      clearTimeout(closeTimer);
      openTimer = setTimeout(openIt, 100);
    });
    root.addEventListener('pointerleave', function () {
      clearTimeout(openTimer);
      closeTimer = setTimeout(function () { closeIt(root); }, 200);
    });
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      if (panel.hidden) openIt(); else closeIt(root);
    });
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        openIt();
        var first = getFocusable(panel)[0];
        if (first) first.focus();
      }
    });
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { closeIt(root); trigger.focus(); }
    });
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) closeIt(root);
    });
  }

  /* ---------------- marquee ---------------- */
  function initMarquee(root) {
    if (!markInit(root, 'marquee')) return;
    var track = root.querySelector('[data-nb-marquee-track]');
    if (!track) return;
    var speed = root.getAttribute('data-nb-marquee-speed') || '32';
    root.style.setProperty('--nb-marquee-speed', speed + 's');
    if (track.children.length) {
      var clone = track.innerHTML;
      track.insertAdjacentHTML('beforeend', clone);
    }
    if (!reduceMotion.matches) root.classList.add('is-running');
  }

  /* ---------------- video in-view ---------------- */
  var videoObserver = null;
  function initVideo(root) {
    each('video[data-nb-video-inview]', root, function (video) {
      if (!markInit(video, 'video-inview')) return;
      if (reduceMotion.matches) return;
      if (!videoObserver) {
        videoObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) entry.target.play().catch(function () {});
            else entry.target.pause();
          });
        }, { threshold: 0.5 });
      }
      videoObserver.observe(video);
    });
  }

  /* ---------------- video hover/focus play ---------------- */
  // nb-c-video's play: 'hover' param marks the <video> with data-nb-video-hover instead of
  // data-nb-video-inview, specifically so the in-view module above never touches it. The
  // trigger is the nearest ancestor carrying data-nb-hover-video (typically the whole card
  // area around the video, not the video itself, so hovering the card plays it, matching
  // the Clean Max mega panel's own hover-open behaviour). Plays on pointer hover
  // (mouseenter) and on keyboard focus (focusin, the trigger needs tabindex="0"), pauses
  // and resets to the start on mouseleave/focusout. Never plays under reduced motion.
  // Absorbed from the header's own hand-rolled assets/nb-shell-header.js, 2026-09-24 (N2):
  // that file duplicated this exact behaviour because nb-ui had no video-hover module.
  function initVideoHover(root) {
    each('[data-nb-hover-video]', root, function (wrap) {
      if (!markInit(wrap, 'video-hover')) return;
      var video = wrap.querySelector('video[data-nb-video-hover]');
      if (!video) return;

      function play() {
        if (reduceMotion.matches) return;
        video.play().catch(function () {});
      }
      function stop() {
        video.pause();
        video.currentTime = 0;
      }

      wrap.addEventListener('mouseenter', play);
      wrap.addEventListener('mouseleave', stop);
      wrap.addEventListener('focusin', play);
      wrap.addEventListener('focusout', stop);
    });
  }

  /* ---------------- hover-expand band ---------------- */
  function initHoverBand(root) {
    if (!markInit(root, 'hover-band')) return;
    var items = root.querySelectorAll('[data-nb-hover-band-item]');
    var canHover = window.matchMedia('(hover: hover)').matches;
    items.forEach(function (item, i) {
      if (canHover) {
        item.addEventListener('pointerenter', function () {
          items.forEach(function (other) {
            other.classList.toggle('is-expanded', other === item);
            other.classList.toggle('is-collapsed', other !== item);
          });
          fire(root, 'nb:change', { index: i });
        });
      } else {
        item.addEventListener('click', function () {
          var willExpand = !item.classList.contains('is-expanded');
          item.classList.toggle('is-expanded', willExpand);
          fire(root, 'nb:change', { index: i });
        });
      }
    });
    root.addEventListener('pointerleave', function () {
      items.forEach(function (i2) {
        i2.classList.remove('is-expanded', 'is-collapsed');
      });
    });
  }

  /* ---------------- tabs ---------------- */
  // Root: data-nb-tabs. Tab controls: data-nb-tab="<key>" (roving tabindex: the active
  // one is tabindex 0, the rest -1). Panels: data-nb-tab-panel="<key>", matched by key
  // against the active tab. data-nb-tabs-hover on the root also activates a tab on
  // pointer hover (100ms delay), in addition to click and arrow keys; without it, hover
  // does nothing and only click/keyboard activate a tab.
  function initTabs(root) {
    if (!markInit(root, 'tabs')) return;
    var hoverEnabled = root.hasAttribute('data-nb-tabs-hover');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-nb-tab]'));
    if (!tabs.length) return;
    var panels = root.querySelectorAll('[data-nb-tab-panel]');
    var hoverTimer = null;

    function activate(tab) {
      var key = tab.getAttribute('data-nb-tab');
      tabs.forEach(function (t) {
        var active = t === tab;
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach(function (p) {
        var match = p.getAttribute('data-nb-tab-panel') === key;
        p.hidden = !match;
      });
      fire(root, 'nb:change', { key: key });
    }

    tabs.forEach(function (tab, i) {
      if (!tab.hasAttribute('tabindex')) tab.setAttribute('tabindex', i === 0 ? '0' : '-1');
      tab.addEventListener('click', function () { activate(tab); });
      if (hoverEnabled) {
        tab.addEventListener('mouseenter', function () {
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(function () { activate(tab); }, 100);
        });
        tab.addEventListener('mouseleave', function () { clearTimeout(hoverTimer); });
      }
      tab.addEventListener('keydown', function (e) {
        var idx = tabs.indexOf(tab);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          var n = tabs[(idx + 1) % tabs.length];
          n.focus();
          activate(n);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          var p2 = tabs[(idx - 1 + tabs.length) % tabs.length];
          p2.focus();
          activate(p2);
        } else if (e.key === 'Home') {
          e.preventDefault();
          tabs[0].focus();
          activate(tabs[0]);
        } else if (e.key === 'End') {
          e.preventDefault();
          tabs[tabs.length - 1].focus();
          activate(tabs[tabs.length - 1]);
        }
      });
    });
  }

  /* ---------------- ask ---------------- */
  // Any element with data-nb-ask (optional data-nb-ask-question) dispatches the
  // cancelable nb:ask event on document with { question }, then, if nothing cancelled
  // it, follows its own href (or /pages/faq when it has none). Delegated on document so
  // it needs no per-element init and works for content injected after load.
  var askDelegated = false;
  function initAsk(root) {
    if (askDelegated) return;
    askDelegated = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-nb-ask]');
      if (!trigger) return;
      var question = trigger.getAttribute('data-nb-ask-question') || '';
      var evt = new CustomEvent('nb:ask', { detail: { question: question }, cancelable: true, bubbles: true });
      document.dispatchEvent(evt);
      if (evt.defaultPrevented) {
        e.preventDefault();
        return;
      }
      var href = trigger.getAttribute('href');
      if (!href) {
        e.preventDefault();
        window.location.href = '/pages/faq';
      }
      // an <a> with a real href navigates on its own; nothing else to do.
    });
  }

  /* ---------------- card-link ---------------- */
  // Any element carrying data-nb-card-href is a whole-card click target: clicking
  // anywhere on it navigates to that URL, EXCEPT a click that lands on a real control
  // inside it (a, button, input, select, label, summary, [role=button], [role=checkbox],
  // or anything explicitly opted out with data-nb-no-card-link), which is left alone so
  // add-to-cart, quantity steppers, remove links, accordion/tab/dropdown/sheet triggers
  // and radio rows keep working. Delegated on document, needs no per-element init, works
  // for content injected after load (cart drawer line items, repainted client-side).
  // Card-links brief, 2026-09-30.
  var cardLinkDelegated = false;
  function initCardLink(root) {
    if (cardLinkDelegated) return;
    cardLinkDelegated = true;
    document.addEventListener('click', function (e) {
      var card = e.target.closest('[data-nb-card-href]');
      if (!card) return;
      var control = e.target.closest(
        'a, button, input, select, label, summary, [role="button"], [role="checkbox"], [data-nb-no-card-link]'
      );
      if (control && card.contains(control)) return;
      var href = card.getAttribute('data-nb-card-href');
      if (!href) return;
      window.location.href = href;
    });
  }

  /* ---------------- cart-add ---------------- */
  // Any element with data-nb-cart-add and data-nb-variant-id (optional
  // data-nb-cart-qty, default 1) posts to /cart/add.js with fetch. On success it
  // dispatches cart:refresh on document (the cart drawer listens and repaints, the header
  // count updates) and clears any error message. On failure it writes the error text into
  // the nearest [data-nb-cart-add-error] element (a caller-provided sibling; if there is
  // none, the module inserts one right after the trigger) instead of throwing. If the
  // trigger has no data-nb-variant-id (e.g. no product wired yet), it does nothing and the
  // element's own href, if it has one, is the fallback: a plain link to the product page.
  // Delegated on document, needs no per-element init, works for content injected after load.
  var cartAddDelegated = false;
  function initCartAdd(root) {
    if (cartAddDelegated) return;
    cartAddDelegated = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-nb-cart-add]');
      if (!trigger) return;
      var variantId = trigger.getAttribute('data-nb-variant-id');
      if (!variantId) return; // no variant wired: fall through to the href fallback
      e.preventDefault();

      var qty = parseInt(trigger.getAttribute('data-nb-cart-qty') || '1', 10) || 1;

      function getErrorEl() {
        var el = trigger.nextElementSibling;
        if (el && el.hasAttribute('data-nb-cart-add-error')) return el;
        el = document.createElement('p');
        el.setAttribute('data-nb-cart-add-error', '');
        el.hidden = true;
        trigger.insertAdjacentElement('afterend', el);
        return el;
      }

      function setError(message) {
        var el = getErrorEl();
        if (message) {
          el.textContent = message;
          el.hidden = false;
        } else {
          el.textContent = '';
          el.hidden = true;
        }
      }

      trigger.setAttribute('aria-busy', 'true');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }] })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || 'Could not add that to cart.');
            return data;
          });
        })
        .then(function () {
          setError(null);
          fire(document, 'cart:refresh', { source: trigger });
        })
        .catch(function (err) {
          setError(err.message || 'Could not add that to cart.');
        })
        .finally(function () {
          trigger.removeAttribute('aria-busy');
        });
    });
  }

  /* ---------------- expo-window ---------------- */
  // Root: any element carrying data-nb-expo-window plus data-nb-expo-start /
  // data-nb-expo-end (YYYY-MM-DD, the metaobject entry's dates, Asia/Singapore
  // local calendar days, no time component). Sets data-nb-expo-state to one of
  // hidden / upcoming / live / ended, computed client-side against the viewer's
  // clock read as Asia/Singapore wall time, and mirrors that into the native
  // `hidden` attribute (hidden and ended take no space; upcoming and live show).
  // upcoming starts 14 days before start_date. live covers start_date..end_date
  // inclusive. Used by H-01 expo, 2026-09-24: Home's expo section and the
  // show-day mode of the marquee both carry this attribute so one clock read
  // drives both.
  function singaporeTodayISO() {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Singapore',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    var y, m, d;
    parts.forEach(function (p) {
      if (p.type === 'year') y = p.value;
      if (p.type === 'month') m = p.value;
      if (p.type === 'day') d = p.value;
    });
    return y + '-' + m + '-' + d;
  }

  function expoState(startISO, endISO, todayISO) {
    if (!startISO || !endISO) return 'hidden';
    var start = new Date(startISO + 'T00:00:00Z').getTime();
    var end = new Date(endISO + 'T00:00:00Z').getTime();
    var today = new Date(todayISO + 'T00:00:00Z').getTime();
    var dayMs = 24 * 60 * 60 * 1000;
    var upcomingFrom = start - 14 * dayMs;
    if (today > end) return 'ended';
    if (today >= start) return 'live';
    if (today >= upcomingFrom) return 'upcoming';
    return 'hidden';
  }

  // Which week this is relative to the show, for a caller (the PDP callout,
  // not yet built) that wants finer copy than upcoming/live/hidden alone.
  // Added H-01 expo 2026-09-24, PM ruling: "week-before" (8-14 days out, next
  // weekend), "show-week" (1-7 days out, this weekend), "show-days" (live,
  // start..end inclusive, today). Blank outside the upcoming/live window.
  function expoWeek(startISO, endISO, todayISO, state) {
    if (state === 'live') return 'show-days';
    if (state !== 'upcoming') return '';
    var dayMs = 24 * 60 * 60 * 1000;
    var start = new Date(startISO + 'T00:00:00Z');
    var today = new Date(todayISO + 'T00:00:00Z').getTime();
    // "Show week" is the calendar week the show starts in, Monday through
    // the day before start_date, per Morgan's ruling 2026-09-26. A raw
    // "within 7 days" countdown put the Saturday/Sunday before that Monday
    // into show-week ("this weekend") when it should still read "next
    // weekend": find the Monday on/before start_date and compare against
    // that date, not against a fixed day count.
    var startDow = start.getUTCDay(); // 0 Sun .. 6 Sat
    var daysSinceMonday = (startDow + 6) % 7; // Mon=0, Sun=6
    var showWeekMonday = start.getTime() - daysSinceMonday * dayMs;
    return today >= showWeekMonday ? 'show-week' : 'week-before';
  }

  function initExpoWindow(root) {
    // Uses the shared per-module markInit() now (2026-09-24): this root can legitimately
    // also carry data-nb-marquee (the standing track doubles as the show-day window), and
    // markInit is per-module precisely so that case works without a dedicated workaround
    // here (it used to have its own data-nb-expo-init flag for exactly this reason).
    if (!markInit(root, 'expo-window')) return;
    var start = root.getAttribute('data-nb-expo-start');
    var end = root.getAttribute('data-nb-expo-end');
    var today = singaporeTodayISO();
    var state = expoState(start, end, today);
    root.setAttribute('data-nb-expo-state', state);
    var week = expoWeek(start, end, today, state);
    if (week) {
      root.setAttribute('data-nb-expo-week', week);
    } else {
      root.removeAttribute('data-nb-expo-week');
    }
    // Which computed states leave this root visible. Default matches the plain
    // expo-panel policy (hidden and ended take no space). A caller with a
    // different policy, e.g. the marquee's standing track, which shows in
    // every state except live, lists its own states in
    // data-nb-expo-visible-states="hidden,upcoming,ended".
    var visibleAttr = root.getAttribute('data-nb-expo-visible-states');
    var visibleStates = visibleAttr ? visibleAttr.split(',') : ['upcoming', 'live'];
    root.hidden = visibleStates.indexOf(state) === -1;
  }

  /* ---------------- toc (utility shared, 2026-09-25) ---------------- */
  // Root: [data-nb-toc] holding [data-nb-toc-source] (the prose column) and a nb-c-jump-list
  // ([data-nb-toc-wrap] with [data-nb-toc-list]). Builds the list from the source's headings
  // (data-nb-toc-selector on the root, default h2), gives each heading an id if it has none,
  // marks the heading currently at the top of the viewport `is-active`. Fewer than two
  // headings hides the whole block. data-nb-toc-limit on the wrap shows that many then a
  // [data-nb-toc-more] toggle. "{n}" in the title and toggle templates becomes the count.
  // data-nb-toc-phone="accordion": the <details> is closed on a phone, open from 900px.
  function slugify(t) {
    return String(t).toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'section';
  }
  function initToc(root) {
    if (!markInit(root, 'toc')) return;
    var source = root.querySelector('[data-nb-toc-source]');
    var wrap = root.querySelector('[data-nb-toc-wrap]');
    var list = root.querySelector('[data-nb-toc-list]');
    if (!source || !wrap || !list) return;
    var heads = Array.prototype.slice.call(source.querySelectorAll(root.getAttribute('data-nb-toc-selector') || 'h2'));
    if (heads.length < 2) { wrap.style.display = 'none'; return; }
    var n = heads.length;
    var limit = parseInt(wrap.getAttribute('data-nb-toc-limit') || '0', 10) || 0;
    var links = [];
    heads.forEach(function (h, i) {
      if (!h.id) {
        var base = slugify(h.textContent), id = base, k = 2;
        while (document.getElementById(id)) { id = base + '-' + k; k += 1; }
        h.id = id;
      }
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.className = 'nb-jump__link';
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      li.appendChild(a);
      if (limit && i >= limit) li.hidden = true;
      list.appendChild(li);
      links.push({ a: a, h: h });
    });
    each('[data-nb-toc-title]', wrap, function (t) {
      t.textContent = (t.getAttribute('data-nb-toc-title-template') || t.textContent).replace('{n}', n);
    });
    var more = wrap.querySelector('[data-nb-toc-more]');
    if (more) {
      if (limit && n > limit) {
        more.textContent = (more.getAttribute('data-nb-toc-more-template') || more.textContent).replace('{n}', n);
        more.hidden = false;
        more.addEventListener('click', function () {
          list.querySelectorAll('li[hidden]').forEach(function (li) { li.hidden = false; });
          more.hidden = true;
          fire(root, 'nb:change', { expanded: true });
        });
      } else { more.hidden = true; }
    }
    var details = wrap.querySelector('details');
    if (details && wrap.getAttribute('data-nb-toc-phone') === 'accordion') {
      var mq = window.matchMedia('(min-width: 900px)');
      var sync = function () { details.open = mq.matches; };
      sync();
      if (mq.addEventListener) mq.addEventListener('change', sync);
      var summary = details.querySelector('summary');
      if (summary) summary.addEventListener('click', function (e) { if (mq.matches) e.preventDefault(); });
    }
    var current = -1, ticking = false;
    function spy() {
      ticking = false;
      var idx = 0;
      links.forEach(function (l, i) { if (l.h.getBoundingClientRect().top <= 140) idx = i; });
      if (idx === current) return;
      current = idx;
      links.forEach(function (l, i) { l.a.classList.toggle('is-active', i === idx); if (i === idx) l.a.setAttribute('aria-current', 'true'); else l.a.removeAttribute('aria-current'); });
    }
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(spy); } }, { passive: true });
    spy();
  }

  /* ---------------- sticky bar (Clean Max PDP, 2026-09-26) ----------------
     Root: [data-nb-sticky-bar]. data-nb-sticky-after points to the id of the
     element to watch (the buy section): the bar shows once that element's
     bottom scrolls above the viewport, hides again when it is back in view.
     One root can hold a desktop chapter nav (links with plain #id hrefs,
     scroll-spied like initToc) and/or a phone price+CTA bar; both live under
     the same root and both react to the same visibility state and the same
     nb:cmax-total-change event dispatched by nb-cmax-buy.js, so the price
     shown here is always the live total from the buy box, never a separate
     hardcoded figure. */
  function initStickyBar(root) {
    if (!markInit(root, 'sticky-bar')) return;
    var afterId = root.getAttribute('data-nb-sticky-after');
    var after = afterId ? document.getElementById(afterId) : null;
    var priceEls = Array.prototype.slice.call(root.querySelectorAll('[data-nb-sticky-price]'));
    var ctaEls = Array.prototype.slice.call(root.querySelectorAll('[data-nb-sticky-cta]'));
    var links = Array.prototype.slice.call(root.querySelectorAll('[data-nb-sticky-link]'));
    /* CRO A2, 2026-09-29: Clean Max desktop chapter bar shows the selected option
       name beside the price. Filled from either nb-cmax-buy.js's own
       nb:cmax-total-change (detail.selected) or the shared option-card module's
       nb:option-change (detail.key/label), whichever the page's buy section fires. */
    var selectedEls = Array.prototype.slice.call(root.querySelectorAll('[data-nb-sticky-selected]'));

    function setVisible(visible) {
      root.classList.toggle('is-visible', visible);
      /* Shared with any floating element that must clear the bar (e.g. the Ask
         nuviebaby launcher): when this bar is pinned to the viewport bottom and
         visible, publish its height on <html> so others can sit above it. Only
         a bottom-pinned bar publishes the var; a top bar (desktop chapter nav
         here) clears it, since nothing needs to dodge a bar at the top. */
      if (visible) {
        var cs = window.getComputedStyle(root);
        if (cs.position === 'fixed' && cs.bottom !== 'auto') {
          document.documentElement.style.setProperty('--nb-sticky-h', (root.offsetHeight + 12) + 'px');
        } else {
          document.documentElement.style.removeProperty('--nb-sticky-h');
        }
      } else {
        document.documentElement.style.removeProperty('--nb-sticky-h');
      }
    }

    if (after && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        // Not intersecting and above the viewport (boundingClientRect.top < 0)
        // means the buy box has scrolled past, so show the bar.
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(after);
    } else {
      setVisible(true);
    }

    /* Scroll-spy the chapter links against their #id targets, same rule as initToc. */
    if (links.length) {
      var targets = links.map(function (a) {
        var id = (a.getAttribute('href') || '').replace('#', '');
        return { a: a, el: id ? document.getElementById(id) : null };
      }).filter(function (t) { return t.el; });
      var current = -1, ticking = false;
      function spy() {
        ticking = false;
        var idx = -1;
        targets.forEach(function (t, i) { if (t.el.getBoundingClientRect().top <= 140) idx = i; });
        if (idx === current) return;
        current = idx;
        targets.forEach(function (t, i) {
          t.a.classList.toggle('is-active', i === idx);
          if (i === idx) t.a.setAttribute('aria-current', 'true'); else t.a.removeAttribute('aria-current');
        });
      }
      window.addEventListener('scroll', function () { if (!ticking) { ticking = true; window.requestAnimationFrame(spy); } }, { passive: true });
      spy();
    }

    function updateFromDetail(detail) {
      priceEls.forEach(function (el) { if (detail.total) el.textContent = el.getAttribute('data-nb-sticky-price-prefix') ? el.getAttribute('data-nb-sticky-price-prefix') + ' ' + detail.total : detail.total; });
      ctaEls.forEach(function (btn) { btn.disabled = !detail.available; });
      /* Job B1 fix, 2026-09-29: board C-11 / sticky-bar spec reads
         "<short option label> · <price>" only, e.g. "Clean730 bundle ·
         $799", never a machine-name prefix. Was appended as a " · "-led
         tail onto a separate machine-name span (removed from the Liquid),
         which is what let the raw product title through; this now writes
         the whole string in one place, with total falling back to Liquid's
         own SSR "<price_prefix> <from-price>" default when there is no
         selection event yet (blank tail was previously left in place). */
      var selected = detail.selected || detail.label;
      var combined = '';
      if (selected && detail.total) combined = selected + ' · ' + detail.total;
      else if (selected) combined = selected;
      else if (detail.total) combined = detail.total;
      if (combined) selectedEls.forEach(function (el) { el.textContent = combined; });
    }

    document.addEventListener('nb:cmax-total-change', function (e) {
      updateFromDetail(e.detail || {});
    });

    document.addEventListener('nb:option-change', function (e) {
      /* Job B1 fix, 2026-09-29: was `total: d.price`, the raw variant cents
         (e.g. 79900), never formatted, so a page whose sticky bar only ever
         hears nb:option-change (no nb:cmax-total-change of its own) printed
         the unformatted number after the option label. d.priceText is the
         same money_without_trailing_zeros string the option card already
         rendered server-side, the site's own format ($799 / RM799, no
         currency code), so this now matches nb:cmax-total-change's output. */
      var d = e.detail || {};
      updateFromDetail({ total: d.priceText || d.price, selected: d.label || d.key, available: true });
    });

    /* The Preorder button here never carries its own price or its own add-to-cart
       logic: it defers to the buy box's own CTA (which already knows the selected
       variant), or scrolls the buy box into view when that CTA cannot be used. */
    ctaEls.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var buyRoot = after ? after.querySelector('[data-nb-add]') || (after.matches('[data-nb-add]') ? after : null) : document.querySelector('[data-nb-cmax-buy] [data-nb-add]');
        if (buyRoot && !buyRoot.disabled) {
          buyRoot.click();
        } else if (after) {
          after.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ---------------- conditional (utility shared, 2026-09-25) ---------------- */
  // Root: any element with data-nb-conditional, data-nb-when="<field name>" and
  // data-nb-when-values="a,b" (comma list). data-nb-when-op: "in" (default, show when the
  // field's value is one of the values), "not-in", "filled" (show when the field has any value).
  // Hidden = native `hidden` attribute, and every control inside is disabled (so a hidden
  // required field never blocks submit and never posts) then re-enabled on show. The field
  // is read from the same <form> (the whole document when there is none): radio and checkbox
  // by checked, a data-nb-pill-value hidden input as a comma list, anything else by value.
  // Re-evaluates on every change/input in that form. Fires nb:change on the root, detail { visible }.
  function condValues(scope, name) {
    var out = [];
    scope.querySelectorAll('[name="' + name.replace(/"/g, '\\"') + '"]').forEach(function (el) {
      if (el.disabled) return;
      var t = (el.type || '').toLowerCase();
      if (t === 'radio' || t === 'checkbox') { if (el.checked) out.push(el.value || 'on'); return; }
      if (el.value === '') return;
      if (el.hasAttribute('data-nb-pill-value')) el.value.split(',').forEach(function (v) { if (v.trim()) out.push(v.trim()); });
      else out.push(el.value);
    });
    return out;
  }
  function condApply(el, scope) {
    var name = el.getAttribute('data-nb-when');
    if (!name) return;
    var values = (el.getAttribute('data-nb-when-values') || '').split(',').map(function (v) { return v.trim(); }).filter(Boolean);
    var op = el.getAttribute('data-nb-when-op') || 'in';
    var cur = condValues(scope, name);
    var hit = cur.some(function (v) { return values.indexOf(v) > -1; });
    var show = op === 'filled' ? cur.length > 0 : (op === 'not-in' ? !hit : hit);
    var was = !el.hidden;
    el.hidden = !show;
    el.querySelectorAll('input, select, textarea, button').forEach(function (c) {
      if (!show) { if (!c.disabled) { c.disabled = true; c.setAttribute('data-nb-cond-disabled', ''); } }
      else if (c.hasAttribute('data-nb-cond-disabled')) { c.disabled = false; c.removeAttribute('data-nb-cond-disabled'); }
    });
    if (was !== show) fire(el, 'nb:change', { visible: show });
  }
  function initConditional(root) {
    if (!markInit(root, 'conditional')) return;
    var scope = root.closest('form') || document;
    var all = function () {
      var els = scope.querySelectorAll('[data-nb-conditional]');
      for (var pass = 0; pass < 2; pass += 1) els.forEach(function (el) { condApply(el, scope); });
    };
    var holder = scope === document ? document.documentElement : scope;
    if (holder.hasAttribute('data-nb-init-conditional-scope')) { all(); return; }
    holder.setAttribute('data-nb-init-conditional-scope', '');
    scope.addEventListener('change', all);
    scope.addEventListener('input', all);
    scope.addEventListener('reset', function () { window.setTimeout(all, 0); });
    all();
  }

  /* ---------------- pill-select (utility shared, 2026-09-25) ---------------- */
  // Root: [data-nb-pill-select], data-nb-pill-multi="false" for a single choice. Options are
  // buttons with data-nb-pill-option and data-value; aria-pressed carries the state. A hidden
  // input[data-nb-pill-value] holds the chosen values as a comma list and fires `change`
  // (bubbling, so a conditional reads it). Preselected values are read from that input.
  // Fires nb:change on the root, detail { values }.
  function initPillSelect(root) {
    if (!markInit(root, 'pill-select')) return;
    var input = root.querySelector('[data-nb-pill-value]');
    var multi = root.getAttribute('data-nb-pill-multi') !== 'false';
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-nb-pill-option]'));
    if (!input || !pills.length) return;
    var start = input.value ? input.value.split(',').map(function (v) { return v.trim(); }) : [];
    pills.forEach(function (p) { p.setAttribute('aria-pressed', start.indexOf(p.getAttribute('data-value')) > -1 ? 'true' : 'false'); });
    pills.forEach(function (p) {
      p.addEventListener('click', function () {
        var on = p.getAttribute('aria-pressed') === 'true';
        if (!multi) pills.forEach(function (o) { if (o !== p) o.setAttribute('aria-pressed', 'false'); });
        p.setAttribute('aria-pressed', on ? 'false' : 'true');
        var values = pills.filter(function (o) { return o.getAttribute('aria-pressed') === 'true'; }).map(function (o) { return o.getAttribute('data-value'); });
        input.value = values.join(',');
        input.dispatchEvent(new Event('change', { bubbles: true }));
        fire(root, 'nb:change', { values: values });
      });
    });
  }

  /* ---------------- upload (utility shared, 2026-09-25) ---------------- */
  // Root: [data-nb-upload]. Limits read from data attributes: data-nb-max-files (default 5),
  // data-nb-max-mb (default 5), data-nb-accept (comma list, "image/*,video/*"). Error
  // templates in data-nb-error-count / -size / -type ("{name}", "{max}" filled in). Parts:
  // input[data-nb-upload-input], the drop label [data-nb-upload-zone], the list
  // [data-nb-upload-list], the message [data-nb-upload-error], and a
  // <template data-nb-upload-item> holding one row ([data-nb-upload-name], [data-nb-upload-size],
  // [data-nb-upload-remove]). Chosen and dropped files are validated, merged, and written back
  // to input.files (DataTransfer) so a native form post carries them. Fires nb:change, detail { files }.
  function initUpload(root) {
    if (!markInit(root, 'upload')) return;
    var input = root.querySelector('[data-nb-upload-input]');
    var zone = root.querySelector('[data-nb-upload-zone]');
    var list = root.querySelector('[data-nb-upload-list]');
    var errEl = root.querySelector('[data-nb-upload-error]');
    var tpl = root.querySelector('template[data-nb-upload-item]');
    if (!input) return;
    var maxFiles = parseInt(root.getAttribute('data-nb-max-files') || '5', 10) || 5;
    var maxMb = parseFloat(root.getAttribute('data-nb-max-mb') || '5') || 5;
    var accept = (root.getAttribute('data-nb-accept') || '').split(',').map(function (a) { return a.trim(); }).filter(Boolean);
    var files = [];
    var fallback = { count: 'Up to {max} files.', size: '{name} is over {max} MB.', type: '{name} is not an accepted file.' };
    function msg(key, name) {
      var t = root.getAttribute('data-nb-error-' + key) || fallback[key];
      return t.replace('{name}', name || '').replace('{max}', key === 'count' ? maxFiles : maxMb);
    }
    function typeOk(f) {
      if (!accept.length) return true;
      return accept.some(function (a) {
        if (a.slice(-2) === '/*') return f.type.indexOf(a.slice(0, -1)) === 0;
        if (a.charAt(0) === '.') return f.name.toLowerCase().slice(-a.length) === a.toLowerCase();
        return f.type === a;
      });
    }
    function fmt(bytes) { return bytes < 1048576 ? Math.max(1, Math.round(bytes / 1024)) + ' KB' : (bytes / 1048576).toFixed(1) + ' MB'; }
    function showErrors(errs) {
      if (!errEl) return;
      errEl.textContent = errs.join(' ');
      errEl.hidden = !errs.length;
    }
    function sync() {
      try {
        var dt = new DataTransfer();
        files.forEach(function (f) { dt.items.add(f); });
        input.files = dt.files;
      } catch (e) { /* older browsers keep the last native selection */ }
      if (list && tpl) {
        list.querySelectorAll('[data-nb-upload-row]').forEach(function (r) { r.remove(); });
        files.forEach(function (f, i) {
          var row = tpl.content.firstElementChild.cloneNode(true);
          row.setAttribute('data-nb-upload-row', '');
          var nm = row.querySelector('[data-nb-upload-name]'); if (nm) nm.textContent = f.name;
          var sz = row.querySelector('[data-nb-upload-size]'); if (sz) sz.textContent = fmt(f.size);
          var rm = row.querySelector('[data-nb-upload-remove]');
          if (rm) rm.addEventListener('click', function () { files.splice(i, 1); showErrors([]); sync(); input.focus(); });
          list.appendChild(row);
        });
      }
      fire(root, 'nb:change', { files: files.slice() });
    }
    function add(incoming) {
      var errs = [];
      Array.prototype.slice.call(incoming).forEach(function (f) {
        if (!typeOk(f)) { errs.push(msg('type', f.name)); return; }
        if (f.size > maxMb * 1048576) { errs.push(msg('size', f.name)); return; }
        var dupe = files.some(function (o) { return o.name === f.name && o.size === f.size && o.lastModified === f.lastModified; });
        if (dupe) return;
        if (files.length >= maxFiles) { var m = msg('count'); if (errs.indexOf(m) === -1) errs.push(m); return; }
        files.push(f);
      });
      showErrors(errs);
      sync();
    }
    input.addEventListener('change', function () { add(input.files); });
    if (zone) {
      ['dragenter', 'dragover'].forEach(function (ev) {
        zone.addEventListener(ev, function (e) { e.preventDefault(); zone.classList.add('is-drag'); });
      });
      ['dragleave', 'dragend'].forEach(function (ev) {
        zone.addEventListener(ev, function () { zone.classList.remove('is-drag'); });
      });
      zone.addEventListener('drop', function (e) {
        e.preventDefault();
        zone.classList.remove('is-drag');
        if (e.dataTransfer && e.dataTransfer.files) add(e.dataTransfer.files);
      });
    }
  }

  /* ---------------- result (utility shared, 2026-09-25) ---------------- */
  // Root: [data-nb-result] holding panels [data-nb-result-panel="<state>"] (all start hidden).
  // Only the panel matching the root's data-state renders; state "" (or an unknown state) shows
  // none and hides the root. A script drives it either by setting data-state on the root, or by
  // dispatching `nb:result` on the root with detail { state, fields: { key: value } }: fields fill
  // every [data-nb-result-field="key"] (empty or missing gets the root's data-nb-result-empty text).
  // data-nb-result-form="<css selector>" names the form to hide while a result shows. Any
  // [data-nb-result-reset] inside clears the state, shows the form again and fires nb:reset.
  // Fires nb:change on the root, detail { state }.
  function initResult(root) {
    if (!markInit(root, 'result')) return;
    var formSel = root.getAttribute('data-nb-result-form');
    var form = formSel ? document.querySelector(formSel) : null;
    var panels = root.querySelectorAll('[data-nb-result-panel]');
    var emptyLabel = root.getAttribute('data-nb-result-empty') || '';
    function apply() {
      var state = root.getAttribute('data-state') || '';
      var found = null;
      panels.forEach(function (p) {
        var on = p.getAttribute('data-nb-result-panel') === state;
        p.hidden = !on;
        if (on) found = p;
      });
      root.hidden = !found;
      if (form) form.hidden = !!found;
      if (found) {
        var h = found.querySelector('h2, h3');
        if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: false }); }
      }
      fire(root, 'nb:change', { state: found ? state : '' });
    }
    function fill(fields) {
      root.querySelectorAll('[data-nb-result-field]').forEach(function (el) {
        var v = fields[el.getAttribute('data-nb-result-field')];
        el.textContent = (v === undefined || v === null || v === '') ? emptyLabel : v;
      });
    }
    root.addEventListener('nb:result', function (e) {
      var d = e.detail || {};
      fill(d.fields || {});
      root.setAttribute('data-state', d.state || '');
    });
    root.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('[data-nb-result-reset]') : null;
      if (!b) return;
      e.preventDefault();
      root.setAttribute('data-state', '');
      fire(root, 'nb:reset');
      if (form) { var f = form.querySelector('input:not([type="hidden"]), select, textarea'); if (f) f.focus(); }
    });
    if (window.MutationObserver) new MutationObserver(apply).observe(root, { attributes: true, attributeFilter: ['data-state'] });
    fill({});
    apply();
  }

  /* ---------------- faq-filter (utility shared, 2026-09-25) ---------------- */
  // Root: [data-nb-faq] around the search, the categories and the groups.
  // input[data-nb-faq-search]; categories anywhere inside carry data-nb-faq-cat="<key>" (the
  // rail rows and the phone chips both); groups are [data-nb-faq-group="<key>"] holding
  // accordion rows (details[data-nb-accordion-row]); [data-nb-faq-count="<key>"] spans show
  // the group's row count (the matching count while a search is typed); [data-nb-faq-empty]
  // is shown when nothing matches. Search is client-side, case and accent insensitive, every
  // word must appear in the row's question or answer. data-nb-faq-mode: "jump" (default, a
  // category scrolls to its group and the current group is marked while scrolling) or
  // "filter" (a category shows only its group, a second click shows all).
  function initFaq(root) {
    if (!markInit(root, 'faq')) return;
    var input = root.querySelector('[data-nb-faq-search]');
    var cats = Array.prototype.slice.call(root.querySelectorAll('[data-nb-faq-cat]'));
    var empty = root.querySelector('[data-nb-faq-empty]');
    var mode = root.getAttribute('data-nb-faq-mode') || 'jump';
    var norm = function (s) { return String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); };
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-nb-faq-group]')).map(function (g) {
      return {
        el: g,
        key: g.getAttribute('data-nb-faq-group'),
        rows: Array.prototype.slice.call(g.querySelectorAll('details[data-nb-accordion-row]')).map(function (r) { return { el: r, text: norm(r.textContent) }; })
      };
    });
    var active = mode === 'filter' || !cats.length ? null : cats[0].getAttribute('data-nb-faq-cat');
    function mark() {
      cats.forEach(function (c) {
        var on = c.getAttribute('data-nb-faq-cat') === active;
        c.classList.toggle('is-active', on);
        if (c.tagName === 'BUTTON') c.setAttribute('aria-pressed', on ? 'true' : 'false');
        else if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
      });
    }
    function apply() {
      var terms = norm(input ? input.value.trim() : '').split(/\s+/).filter(Boolean);
      var total = 0;
      groups.forEach(function (g) {
        var shown = 0;
        g.rows.forEach(function (r) {
          var ok = terms.every(function (t) { return r.text.indexOf(t) > -1; });
          r.el.hidden = !ok;
          if (ok) shown += 1;
        });
        var off = mode === 'filter' && active && g.key !== active;
        g.el.hidden = shown === 0 || !!off;
        if (!g.el.hidden) total += shown;
        root.querySelectorAll('[data-nb-faq-count="' + g.key + '"]').forEach(function (c) { c.textContent = shown; });
      });
      if (empty) empty.hidden = total > 0;
    }
    cats.forEach(function (c) {
      c.addEventListener('click', function (e) {
        var key = c.getAttribute('data-nb-faq-cat');
        e.preventDefault();
        if (mode === 'filter') { active = active === key ? null : key; }
        else {
          active = key;
          var g = groups.filter(function (x) { return x.key === key; })[0];
          if (g) g.el.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
        }
        mark();
        apply();
      });
    });
    if (mode !== 'filter' && groups.length) {
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          var cur = null;
          groups.forEach(function (g) { if (!g.el.hidden && g.el.getBoundingClientRect().top <= 140) cur = g.key; });
          if (cur && cur !== active) { active = cur; mark(); }
        });
      }, { passive: true });
    }
    if (input) {
      input.addEventListener('input', apply);
      input.addEventListener('keydown', function (e) { if (e.key === 'Escape' && input.value) { input.value = ''; apply(); } });
    }
    mark();
    apply();
  }

  /* ---------------- machine-toggle (C-08, go-live 2026-09-28) ----------------
     Root: [data-nb-machine-toggle] with [data-nb-machine-toggle-default] ("cleanmax" |
     "clean" | "none"). Tiles: [data-nb-machine-toggle-tile] with data-value and
     aria-pressed. Click selects a tile (single-select); clicking the already-selected
     tile clears back to "none" (shows everything), matching a board that draws one tile
     pre-selected rather than a genuine unselected state. Filters every [data-nb-fits] on
     the page (space list of machines it fits, e.g. "clean cleanmax"): hidden unless the
     selected value is blank/none or is one of its listed machines. Any [data-nb-fits-grid]
     collapses, and anything with [data-nb-fits-heading-for] pointing at that grid's id
     hides too, the moment every [data-nb-fits] card inside it is hidden. Several toggle
     instances on one page (should not normally happen, but a founder can add more than
     one section) all drive the same shared card set: the last one clicked wins. */
  function applyMachineFilter(value) {
    each('[data-nb-fits]', document, function (card) {
      if (!value) { card.hidden = false; return; }
      var fits = (card.getAttribute('data-nb-fits') || '').split(/\s+/).filter(Boolean);
      card.hidden = fits.length > 0 && fits.indexOf(value) === -1;
    });
    each('[data-nb-fits-grid]', document, function (grid) {
      var cards = grid.querySelectorAll('[data-nb-fits]');
      var anyVisible = true;
      if (cards.length) {
        anyVisible = Array.prototype.some.call(cards, function (c) { return !c.hidden; });
      }
      grid.hidden = !anyVisible;
      var id = grid.getAttribute('id');
      if (id) {
        each('[data-nb-fits-heading-for="' + id + '"]', document, function (heading) {
          heading.hidden = !anyVisible;
        });
      }
    });
  }

  /* mode "sections" (support hub go-live, 2026-09-29): show/hide [data-machine] elements
     inside the nearest [data-nb-machine-scope] block instead of filtering [data-nb-fits]
     cards. An element with no data-machine ancestor is untouched and always shows. Param
     only, added to the existing component per BUILD-CONVENTIONS ("never fork it"). */
  function applyMachineSections(value) {
    each('[data-nb-machine-scope]', document, function (scope) {
      // CRO A4b fix: an option-card radio checked inside the machine that is about to
      // hide (e.g. "Just this box" duplicated per machine on a two-product page like
      // HEPA) needs its selection carried to the equivalent card in the machine being
      // shown, and a change event fired, or the price/CTA/gallery are left pointing at
      // a hidden, no-longer-selectable product. Read the checked state before toggling.
      var checkedInput = scope.querySelector('input[type="radio"]:checked');
      var checkedKey = null;
      if (checkedInput) {
        var checkedCard = checkedInput.closest('[data-nb-option-key]');
        var checkedHidden = checkedInput.closest('[data-machine]');
        if (checkedCard && checkedHidden && checkedHidden.getAttribute('data-machine') !== value) {
          checkedKey = checkedCard.getAttribute('data-nb-option-key');
        }
      }
      each('[data-machine]', scope, function (el) {
        el.hidden = !!value && el.getAttribute('data-machine') !== value;
      });
      if (checkedKey) {
        var target = scope.querySelector(
          '[data-machine="' + value + '"] [data-nb-option-key="' + checkedKey + '"] input[type="radio"]'
        );
        if (target && !target.checked) {
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
    /* [data-hide-on-machine="clean"]: hides on that exact value, e.g. the PDP guide card
       which the Support hub spec says never shows in the Clean view. Support hub/article
       go-live, 2026-09-29. */
    each('[data-hide-on-machine]', document, function (el) {
      var hideValue = el.getAttribute('data-hide-on-machine');
      el.hidden = !!value && value === hideValue;
    });
  }

  /* ---------------- support-article: On this page list (go-live 2026-09-29) ----------------
     Builds [data-nb-on-this-page-list] from the h2 headings actually present in
     [data-nb-machine-scope], so it works however many headings a machine section shows/hides.
     Static list built once at init; not re-built on toggle switch (out of scope this round,
     acceptable since headings rarely differ in count between machine views on these articles). */
  function initSupportArticleToc(root) {
    var list = root.querySelector('[data-nb-on-this-page-list]');
    var scope = document.querySelector('[data-nb-machine-scope]');
    if (!list || !scope) return;
    var headings = scope.querySelectorAll('h2[id]');
    headings.forEach(function (h) {
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      a.className = 'text-caption font-sans leading-[18px] text-charcoal hover:text-electric';
      list.appendChild(a);
    });
  }

  function setMachineToggleState(root, value) {
    root.setAttribute('data-nb-machine-toggle-value', value || '');
    each('[data-nb-machine-toggle-tile]', root, function (tile) {
      tile.setAttribute('aria-pressed', tile.getAttribute('data-value') === value ? 'true' : 'false');
    });
    var mode = root.getAttribute('data-nb-machine-toggle-mode') || 'filter';
    if (mode === 'sections') {
      applyMachineSections(value);
    } else {
      applyMachineFilter(value);
    }
    fire(root, 'nb:change', { value: value });
  }

  function initMachineToggle(root) {
    if (!markInit(root, 'machine-toggle')) return;
    var defaultValue = root.getAttribute('data-nb-machine-toggle-default') || 'none';
    if (defaultValue === 'none') defaultValue = '';
    each('[data-nb-machine-toggle-tile]', root, function (tile) {
      tile.addEventListener('click', function () {
        var value = tile.getAttribute('data-value');
        var current = root.getAttribute('data-nb-machine-toggle-value') || '';
        setMachineToggleState(root, current === value ? '' : value);
      });
    });
    setMachineToggleState(root, defaultValue);
  }

  /* ---------------- option group (CRO A1, 2026-09-29) ---------------- */
  // Scoped to a role="radiogroup" wrapper carrying data-nb-option-group. Listens for
  // `change` on its radio inputs (each row is an nb-c-option-card <label>), toggles the
  // row's `is-selected` class + aria-checked, and dispatches `nb:option-change` on the
  // group root with {key, variantId, galleryImage, price}.
  function setOptionCardSelected(card, isSelected) {
    card.classList.toggle('is-selected', isSelected);
    var input = card.querySelector('input[type="radio"]');
    if (input) input.setAttribute('aria-checked', isSelected ? 'true' : 'false');
  }

  function initOptionGroup(root) {
    if (!markInit(root, 'option-group')) return;
    var cards = Array.prototype.slice.call(root.querySelectorAll('.nb-c-option-card'));
    cards.forEach(function (card) {
      var input = card.querySelector('input[type="radio"]');
      if (!input) return;
      input.addEventListener('change', function () {
        if (!input.checked) return;
        cards.forEach(function (other) {
          var otherInput = other.querySelector('input[type="radio"]');
          setOptionCardSelected(other, otherInput === input);
        });
        fire(root, 'nb:option-change', {
          key: card.getAttribute('data-nb-option-key'),
          variantId: card.getAttribute('data-nb-option-variant-id'),
          galleryImage: card.getAttribute('data-nb-gallery-image'),
          price: card.getAttribute('data-nb-option-price'),
          priceText: card.getAttribute('data-nb-option-price-text'),
          compareText: card.getAttribute('data-nb-option-compare-text'),
          saveText: card.getAttribute('data-nb-option-save-text'),
          label: card.getAttribute('data-nb-option-label')
        });
      });
    });
  }

  /* ---------------- option group: gallery follows option + ?option= preselect
     (CRO A4, 2026-09-29) ---------------- */
  // Generic, reused by any page with a [data-nb-option-group] radiogroup: on
  // nb:option-change, swaps the nearest [data-nb-gallery]'s [data-nb-gallery-main]
  // image to detail.galleryImage. On load, ?option=<key> checks the matching card
  // (data-nb-option-key) and fires the same change, so the gallery/price/pack state
  // preselects from the URL without extra markup on the page.
  function swapGalleryMain(root, url) {
    if (!url) return;
    var gallery = (root.closest && root.closest('[data-nb-gallery]')) || document.querySelector('[data-nb-gallery]');
    if (!gallery) return;
    var main = gallery.querySelector('[data-nb-gallery-main]');
    if (!main) return;
    var img = main.tagName === 'IMG' ? main : main.querySelector('img');
    if (img) {
      img.src = url;
      img.removeAttribute('srcset');
    } else {
      main.style.backgroundImage = 'url(' + url + ')';
    }
  }

  // Also syncs the page's own inline price line ([data-nb-acc-price] .nb-c-price__now)
  // and the main/sticky "Add to cart" buttons' variant id, so a pack selection is
  // reflected in price and actually adds the right product, not just the gallery.
  function syncOptionTargets(root, detail) {
    swapGalleryMain(root, detail.galleryImage);
    if (detail.priceText) {
      var priceBlock = document.querySelector('[data-nb-acc-price]');
      var priceEl = priceBlock && priceBlock.querySelector('.nb-c-price__now');
      if (priceEl) priceEl.textContent = detail.priceText;
      // CRO fix (2026-09-29): the strike-through compare-at and the Save badge were left
      // showing the single-box card's values after an option-card swap, because only the
      // now-price text node was synced. The option card now carries its own compare/save
      // text via data-nb-option-compare-text / data-nb-option-save-text (blank when there
      // is no saving on that option); rebuild both from the selected card, hiding either
      // node when the selected option has no saving.
      if (priceBlock) {
        var priceRow = priceEl && priceEl.parentElement;
        var wasEl = priceBlock.querySelector('.nb-c-price__was');
        if (detail.compareText) {
          if (!wasEl && priceRow) {
            wasEl = document.createElement('span');
            wasEl.className = 'nb-c-price__was font-sans text-[16px] text-mute line-through';
            priceRow.appendChild(wasEl);
          }
          if (wasEl) {
            wasEl.textContent = detail.compareText;
            wasEl.hidden = false;
          }
        } else if (wasEl) {
          wasEl.hidden = true;
        }
        var badgeEl = priceBlock.querySelector('.nb-c-badge');
        if (detail.saveText) {
          if (!badgeEl) {
            badgeEl = document.createElement('span');
            badgeEl.className = 'nb-c-badge bg-sky text-cobalt inline-flex items-center rounded-[999px] text-[13px] font-medium px-[12px] py-[6px]';
            priceBlock.appendChild(badgeEl);
          }
          badgeEl.textContent = detail.saveText;
          badgeEl.hidden = false;
        } else if (badgeEl) {
          badgeEl.hidden = true;
        }
      }
    }
    if (detail.variantId) {
      each('[data-nb-acc-cta]', document, function (btn) {
        btn.setAttribute('data-nb-variant-id', detail.variantId);
        btn.disabled = false;
      });
    }
    // Fit tag (only fits Clean Max / Clean): shown only while the pack option is
    // selected. data-nb-fit-tag-for="pack" on each machine's tag instance; the
    // machine-toggle "sections" module already picks which machine's tag shows.
    each('[data-nb-fit-tag-for]', document, function (tag) {
      tag.hidden = tag.getAttribute('data-nb-fit-tag-for') !== detail.key;
    });
  }

  function initOptionGallerySync(root) {
    if (!markInit(root, 'option-gallery-sync')) return;
    root.addEventListener('nb:option-change', function (e) {
      syncOptionTargets(root, e.detail || {});
    });
  }

  function initOptionPreselect(root) {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (err) {
      return;
    }
    var key = params.get('option');
    if (!key) return;
    each('[data-nb-option-group]', root, function (group) {
      // CRO A4b fix: an option key can match more than one card when it is duplicated
      // per machine (e.g. HEPA's "pack" row inside both data-machine scopes). Prefer
      // whichever match is actually visible (its data-machine ancestor, if any, isn't
      // hidden) over the first one in DOM order, or the URL preselect can check a
      // hidden card and leave the visible row looking unselected.
      var cards = group.querySelectorAll('[data-nb-option-key="' + key.replace(/"/g, '') + '"]');
      var card = null;
      cards.forEach(function (candidate) {
        if (card) return;
        var machineAncestor = candidate.closest('[data-machine]');
        if (!machineAncestor || !machineAncestor.hidden) card = candidate;
      });
      if (!card) card = cards[0];
      if (!card) return;
      var input = card.querySelector('input[type="radio"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /* ---------------- init all ---------------- */
  function initAll(root) {
    each('[data-nb-accordion]', root, initAccordion);
    each('[data-nb-carousel]', root, initCarousel);
    initSheets(root);
    initDrawers(root);
    each('[data-nb-dropdown]', root, initDropdown);
    each('[data-nb-marquee]', root, initMarquee);
    initVideo(root);
    initVideoHover(root);
    each('[data-nb-hover-band]', root, initHoverBand);
    each('[data-nb-tabs]', root, initTabs);
    initAsk(root);
    initCardLink(root);
    initCartAdd(root);
    each('[data-nb-expo-window]', root, initExpoWindow);
    each('[data-nb-toc]', root, initToc);
    each('[data-nb-sticky-bar]', root, initStickyBar);
    each('[data-nb-conditional]', root, initConditional);
    each('[data-nb-pill-select]', root, initPillSelect);
    each('[data-nb-upload]', root, initUpload);
    each('[data-nb-result]', root, initResult);
    each('[data-nb-faq]', root, initFaq);
    each('[data-nb-machine-toggle]', root, initMachineToggle);
    each('[data-nb-option-group]', root, initOptionGroup);
    each('[data-nb-option-group]', root, initOptionGallerySync);
    initOptionPreselect(root);
    each('[data-nb-on-this-page]', root, initSupportArticleToc);
  }

  document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target || document); });
})();
